import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout, { adminFetch, SectionTitle, AdminCard, useAdmin } from '../AdminLayout';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function formatValue(v) {
  if (v === null || v === undefined) {
    return <em style={{ color: '#8A8A8A' }}>aucune valeur</em>;
  }
  if (typeof v === 'object') {
    return (
      <code style={{ fontSize: '0.82rem', wordBreak: 'break-all' }}>
        {JSON.stringify(v)}
      </code>
    );
  }
  return String(v);
}

// ── Sous-composant liste des changements ──────────────────────────────────────

function ChangesList({ changes }) {
  if (!changes || changes.length === 0) {
    return <p style={{ margin: '4px 0', fontStyle: 'italic', color: '#8A8A8A' }}>Aucun détail de changement enregistré.</p>;
  }
  return (
    <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none' }}>
      {changes.map((c, i) => (
        <li key={i} style={{ marginBottom: 6, fontSize: 13, lineHeight: 1.5 }}>
          <strong style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.field}</strong>
          {' : '}
          {formatValue(c.oldValue)}
          <span style={{ margin: '0 6px', color: '#D4724A', fontWeight: 700 }}>→</span>
          {formatValue(c.newValue)}
        </li>
      ))}
    </ul>
  );
}

// ── Composant principal ────────────────────────────────────────────────────────

export default function AideHistoryPage() {
  return (
    <AdminLayout title="Historique des modifications">
      <AideHistoryContent />
    </AdminLayout>
  );
}

// ── Contenu ────────────────────────────────────────────────────────────────────

function AideHistoryContent() {
  const { id } = useParams();
  const { dark, border } = useAdmin();
  const textColor  = dark ? '#e0e0e0' : '#2D2D2D';
  const mutedColor = '#8A8A8A';

  const [entries, setEntries] = useState([]);
  const [aide, setAide]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // ── Chargement en parallèle aide + historique ──────────────────────────────

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    (async () => {
      try {
        const [aideRes, histRes] = await Promise.all([
          adminFetch(`/api/admin/aides/catalogue/${encodeURIComponent(id)}`),
          adminFetch(`/api/admin/aides/catalogue/${encodeURIComponent(id)}/history?limit=200`),
        ]);

        const aideBody = await aideRes.json().catch(() => null);
        const histBody = await histRes.json().catch(() => null);

        if (!cancelled) {
          if (aideRes.ok && aideBody) {
            setAide(aideBody.aide || aideBody);
          }
          if (histRes.ok && histBody) {
            setEntries(histBody.entries || histBody || []);
          } else if (!histRes.ok) {
            setError(histBody?.error || `Erreur lors du chargement de l'historique (${histRes.status}).`);
          }
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Erreur de chargement de l'historique.");
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [id]);

  // ── Liens de navigation ────────────────────────────────────────────────────

  const linkStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '10px 18px', borderRadius: 10,
    border: `1px solid ${border}`,
    background: 'transparent', color: dark ? '#e0e0e0' : '#2D2D2D',
    fontSize: 14, fontWeight: 500, textDecoration: 'none',
  };

  // ── Chargement ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div>
        <SectionTitle>Chargement de l'historique…</SectionTitle>
        <AdminCard>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                style={{
                  height: 80, borderRadius: 8,
                  background: dark ? '#1e1e3a' : '#F5F3F0',
                  opacity: 0.4 + i * 0.1,
                }}
              />
            ))}
          </div>
        </AdminCard>
      </div>
    );
  }

  // ── Rendu ───────────────────────────────────────────────────────────────────

  const nomAide = aide?.nom ?? id;

  return (
    <div>
      {/* En-tête */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        flexWrap: 'wrap', gap: 16, marginBottom: 24,
      }}>
        <SectionTitle>
          Historique : {nomAide}
        </SectionTitle>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Link to={`/admin/aides/catalogue/${id}`} style={linkStyle}>
            ← Retour à l'édition
          </Link>
          <Link to="/admin/aides/catalogue" style={linkStyle}>
            Liste des aides
          </Link>
        </div>
      </div>

      {/* Erreur */}
      {error && (
        <div
          role="alert"
          style={{
            background: '#FEE2E2', border: '1px solid rgba(192,57,43,0.4)',
            borderRadius: 12, padding: '14px 20px', marginBottom: 20,
            color: '#991B1B', fontSize: 14, display: 'flex', alignItems: 'center', gap: 10,
          }}
        >
          <span style={{ fontSize: 18 }}>⚠️</span>
          {error}
        </div>
      )}

      <AdminCard>
        {/* Aucune entrée */}
        {entries.length === 0 && !error && (
          <p style={{ color: mutedColor, fontStyle: 'italic' }}>
            Aucune modification enregistrée pour cette aide.
          </p>
        )}

        {/* Audit trail */}
        {entries.length > 0 && (
          <div>
            <p style={{ color: mutedColor, fontSize: 13, marginBottom: 20, marginTop: 0 }}>
              <strong style={{ color: textColor }}>{entries.length}</strong>
              {' '}modification{entries.length > 1 ? 's' : ''}
              {' '}enregistrée{entries.length > 1 ? 's' : ''}.
            </p>

            <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {entries.map((entry, idx) => (
                <li
                  key={idx}
                  style={{
                    borderLeft: '3px solid #D4724A',
                    paddingLeft: '1.25rem',
                    marginBottom: '2rem',
                  }}
                >
                  {/* Méta-données de l'entrée */}
                  <header style={{ marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 700, color: textColor, fontSize: 14 }}>
                      {formatDate(entry.createdAt)}
                    </span>
                    <span style={{ color: mutedColor, fontSize: 13 }}>
                      {' — par '}
                      <strong style={{ color: textColor }}>{entry.modifiedBy || 'inconnu'}</strong>
                      {entry.modifierEmail && (
                        <span> ({entry.modifierEmail})</span>
                      )}
                    </span>
                    {entry.source && (
                      <span style={{ marginLeft: 8 }}>
                        <span style={{ color: mutedColor, fontSize: 13 }}>• source : </span>
                        <code style={{ fontSize: 12, background: dark ? '#0f0f23' : '#F0EDE8', padding: '1px 5px', borderRadius: 3 }}>
                          {entry.source}
                        </code>
                      </span>
                    )}
                  </header>

                  {/* Raison */}
                  {entry.reason && (
                    <p style={{ margin: '4px 0 8px', fontSize: 13, color: textColor }}>
                      <strong>Raison :</strong> {entry.reason}
                    </p>
                  )}

                  {/* Changements détaillés */}
                  <ChangesList changes={entry.changes} />
                </li>
              ))}
            </ol>
          </div>
        )}
      </AdminCard>

      {/* Lien retour bas de page */}
      <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${border}` }}>
        <Link
          to={`/admin/aides/catalogue/${id}`}
          style={{ color: mutedColor, fontSize: 14, textDecoration: 'none' }}
        >
          ← Retour à l'édition de « {nomAide} »
        </Link>
      </div>
    </div>
  );
}
